import Loading from 'components/loading';
import { EventProvider, useEvent } from 'contexts/event';
import React, { useEffect, useRef, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { faCalendar, faEllipsisH, faExternalLink, faHome, faLocation, faShoppingCart } from '@fortawesome/pro-regular-svg-icons';
import 'style/mobileView.scss';
import { useSession } from 'contexts/session';
import NavigationBar from 'components/navigationBar';
import { useNotifications } from 'contexts/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import logo from 'assets/logo-navy.png';

export async function loader({params}) {
    return params;
}

function Event() {
    const loaderData = useLoaderData();
    const [view, setView] = useState('home');

    const navButtons = [
            {id: 'home', icon: faHome, label: 'Home'},
            {id: 'schedule', icon: faCalendar, label: 'Schedule'},
            {id: 'maps', icon: faLocation, label: 'Maps'},
            {id: 'vendors', icon: faShoppingCart, label: 'Vendors'},
            {id: 'more', icon: faEllipsisH, label: 'More'},
        ];

    return (
        <div className='mobile-view within-portal'>
            <EventProvider eventId={loaderData.eventId} >
                {view === 'home' && <EventHome />}
                {view === 'schedule' && <EventSchedule />}
                {view === 'maps' && <EventMaps />}
                {view === 'vendors' && <EventVendors />}
                {view === 'more' && <EventMore />}
                <NavigationBar buttons={navButtons} selected={view} onSetView={setView} />
            </EventProvider>
        </div>
    )
}

function EventHome() {
    const { eventLoading, event } = useEvent();

    return (
        <div className='view-container home'>
            {eventLoading ? <Loading /> 
            : 
            <>
                <h1>{event.name}</h1>
                {event.images.length > 0 ? <img src={event.images[0].imageData} /> : <div>No Image</div>}
                <h3>
                    {moment(event.startDate).isSame(event.endDate, 'day') 
                        ? moment(event.startDate).format('dddd, MMMM Do YYYY')
                        : `${moment(event.startDate).format('dddd, MMMM Do YYYY')}-${moment(event.endDate).format('dddd, MMMM Do YYYY')}`}
                </h3>
                <p>{event.description}</p>
            </>
            }
        </div>
    )
}

function EventSchedule() {
    const { eventLoading, event } = useEvent();
    const [schedule, setSchedule] = useState([]);

    useEffect(() => {
        if (!eventLoading && event.schedule.length > 0) {
            setSchedule(JSON.parse(event.schedule))
        }
    }, [eventLoading])

    return (
        <div className='view-container schedule'>
            <h1>Schedule</h1>
            {schedule.length === 0 ? <div>No schedule</div> :
                <ul className='schedule-day'>
                    {schedule.map(s => 
                        <li key={s.id}>
                            <div className='day-title'>{s.title}</div>
                            <div>
                                <ul className='schedule-data'>
                                    {s.data.map(d => <li key={d.id}>{`${d.startTime} - ${d.endTime} : ${d.label}`}</li>)}
                                </ul>
                            </div>
                        </li>
                    )}
                </ul>
            }
        </div>
    )
}

function EventMaps() {
    const { eventLoading, maps } = useEvent();
    const containerRefs = useRef({}); // Store refs for each mapContainer
    const [imageSizes, setImageSizes] = useState({}); // Store dimensions of background images

    // Load image dimensions
    useEffect(() => {
        const loadImageSizes = async () => {
            const sizes = {};
            for (const map of maps) {
                if (map.backgroundImage) {
                    const img = new Image();
                    img.src = map.backgroundImage;
                    await img.decode(); // Wait for the image to load
                    sizes[map.id] = {
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    };
                }
            }
            setImageSizes(sizes);
        };

        if (maps.length > 0) {
            loadImageSizes();
        }
    }, [maps]);

    return (
        <div className="view-container maps">
            <h1>Maps</h1>
            {maps.length === 0 ? (
                <div>No maps</div>
            ) : (
                <div>
                    {maps.map((m) => {
                        const imageSize = imageSizes[m.id];

                        if (!imageSize) {
                            // Wait for image dimensions to load
                            return <div key={m.id}>Loading map...</div>;
                        }

                        // Debugging: Log container and image sizes
                        console.log('Image Size:', imageSize);

                        return (
                            <div key={m.id}>
                                <div>{m.label}</div>
                                <div
                                    className="mapContainer"
                                    ref={(el) => (containerRefs.current[m.id] = el)} // Assign ref for this mapContainer
                                    style={{
                                        position: 'relative',
                                        width: '100%',
                                        paddingTop: `${(imageSize.height / imageSize.width) * 100}%`, // Maintain aspect ratio
                                        backgroundImage: `url(${m.backgroundImage})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center',
                                    }}
                                >
                                    <EventMapElements 
                                        map={m} 
                                        imageSize={imageSize}
                                        container={containerRefs.current[m.id]}
                                         />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function EventMapElements({map, imageSize, container}) {
    const [scale, setScale] = useState(1);
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);

    const updateSize = () => {
        if (container) {
            const rect = container.getBoundingClientRect();

            // Calculate scaling factors for the background image
            const scaleX = rect.width / imageSize.width;
            const scaleY = rect.height / imageSize.height;
            const svgScale = Math.min(scaleX, scaleY); // Use the smaller scale to maintain aspect ratio

            // Calculate offsets to center the background image
            setScale(svgScale);
            setOffsetX((rect.width - imageSize.width * svgScale) / 2);
            setOffsetY((rect.height - imageSize.height * svgScale) / 2);
        }
    };

    useEffect(() => {
        // update on mount
        updateSize();

        window.addEventListener('resize', updateSize);
        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, [container]);
    
    return (
        <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            viewBox='0,0,1024,768'
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
            }}
        >
           
            {map.elements.map((element, idx) => {
                const normalizedX = element.x;
                const normalizedY = element.y;
                const normalizedWidth = element.w;
                const normalizedHeight = element.h;

                let elem = null;
                switch (element.shape) {
                    case 'box':
                        elem = (
                            <rect
                                key={idx}
                                x={normalizedX}
                                y={normalizedY}
                                width={normalizedWidth}
                                height={normalizedHeight}
                                stroke="#000"
                                fill="#FFF"
                            />
                        );
                        break;
                    case 'circle':
                        elem = (
                            <circle
                                key={idx}
                                cx={normalizedX}
                                cy={normalizedY}
                                r={element.w / 2} // Radius is half the width
                                stroke="#000"
                                fill="#FFF"
                            />
                        );
                        break;
                    default:
                        break;
                }
                return elem;
            })}
            {map.elements.map((element, idx) => {
                if (element.shape === 'box' && element.row.length > 0) {
                    const normalizedX = element.x;
                    const normalizedY = element.y;
                    const normalizedWidth = element.w;
                    const normalizedHeight = element.h;

                    return (
                        <text
                            key={`text-${idx}`}
                            x={normalizedX + normalizedWidth / 2}
                            y={normalizedY + normalizedHeight / 2}
                            textAnchor="middle"
                            alignmentBaseline="middle"
                            style={{ fontSize: '14px', fill: '#000' }}
                        >
                            {`${element.row}${element.num}`}
                        </text>
                    );
                }
                return null;
            })}
            
        </svg>
    )
}

function EventVendors() {
    const { eventLoading, event, maps } = useEvent();

    const getVendorReservations = (vendorId) => {
        //debugger
        const reservationsByMap = maps.map(map => {
            const reservedElements = map.reservations
                .filter(reservation => reservation.vendorId === vendorId)
                .map(reservation => {
                    const element = map.elements.find(el => el.id === reservation.eventMapElementId);
                    return element ? `${element.row}-${element.num}` : null;
                })
                .filter(Boolean); // Remove null values
            return reservedElements.length > 0 ? `${map.label} ${reservedElements.join(', ')}` : null;
        }).filter(Boolean); // Remove empty maps
        return reservationsByMap;
    };

    return (
        <div className='view-container vendors'>
            <h1>Vendors</h1>
            {event.vendors.length === 0 ? <div>No vendors</div> :
                <ul>
                {event.vendors.map(vendor => {
                        const reservations = getVendorReservations(vendor.id);
                        return (
                            <li key={vendor.id}>
                                <div>{vendor.name}</div>
                                <div className='vendor-reservations'>
                                {reservations.length > 0 && (
                                    <>
                                    {reservations.map((res, index) => (
                                        <div key={index}>{res}</div>
                                    ))}
                                    </>
                                )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            }
        </div>
    )
}

function EventMore() {
    const { event } = useEvent();
    const { tenant } = useSession();
    const { requestNotificationPermission, permission } = useNotifications();
    const navigate = useNavigate();

    return (
        <div className='view-container more'>
            <h1>More</h1>
            <div>
                <label>Notifications</label>
                <div>
                    {permission === 'granted' 
                        ? <><FontAwesomeIcon icon={faCheckCircle} /> Enabled</>
                        : permission === 'denied' ?
                            <p>Notificaitons are dsisabled, use your browser site settings to enable them.</p>
                    : <button onClick={requestNotificationPermission}>Allow</button>
                    }
                </div>
            </div>
            <hr />
            <label>Join the club</label>
            <div>
                <p>The {event.name} is put on by the {tenant.name} club. If you like this show
                    and want to become a member of the club, consider joining.
                </p>
                <button onClick={() => navigate('/portal/join')}>Join the club</button>
            </div>
            <hr/>
            <label>Event Notes</label>
            <div style={{ whiteSpace: 'pre-wrap' }}>{event.viewerNotes}</div>
            <hr/>
            <label>About Middletwin</label>
            <p>Middletwin is the all-in-one platform for managing car shows and car clubs, 
                streamlining events, registrations, and ticketing. {event.name} is powered 
                by Middletwin. Learn more by visiting&nbsp;
                <a href='https://www.middletwin.com' target='_blank' rel='noreferrer'>
                    https://www.middletwin.com <FontAwesomeIcon icon={faExternalLink} />
                </a>
                <img 
                    src={logo} 
                    alt='Middletwin logo'
                    style={{ maxWidth: '100%', height: 'auto', display: 'block', marginTop: '1em' }}
                />
            </p>
        </div>
    )
}

export default Event;